import { useState } from 'react';
import { Check } from 'lucide-react';
import { Menu, MenuItem, Popover } from '@vd/ui';
import type { ProjectTicketType, TicketTypeRef } from '@vd/types';
import styles from './TicketDetail.module.css';

/**
 * What the Type control offers (TOADO-434): the project's visible types, and the
 * ticket's own type even when the project has since hidden it or it is missing
 * from the list, so the control never claims a ticket has no type when it has one.
 */
export function ticketTypeOptions(
  current: TicketTypeRef | undefined,
  types: readonly ProjectTicketType[] | undefined,
): Array<{ id: string; name: string }> {
  const offered = (types ?? []).filter((type) => !type.hidden || type.id === current?.id);
  const missing = current && !offered.some((type) => type.id === current.id)
    ? [{ id: current.id, name: current.name }]
    : [];
  return [...offered.map((type) => ({ id: type.id, name: type.name })), ...missing];
}

/**
 * Bug, improvement or new feature, beside who has the ticket (TOADO-434).
 *
 * Built the way the column control at the top of the page is - the same pill,
 * opening the same menu with a tick on the current choice - so the two controls
 * that say what state a ticket is in read as a pair. "No type" is a choice like
 * any other: an untyped ticket is a legal state.
 */
export function TicketTypeControl({
  current,
  types,
  disabled,
  onChange,
}: {
  current: TicketTypeRef | undefined;
  types: readonly ProjectTicketType[] | undefined;
  disabled: boolean;
  onChange: (typeId: string | null) => void;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const choose = (typeId: string | null) => {
    setAnchor(null);
    if (typeId !== (current?.id ?? null)) onChange(typeId);
  };
  const tick = (selected: boolean) => (selected ? <Check size={14} /> : <span style={{ width: 14 }} />);
  return (
    <div className={styles.typeGroup}>
      <span className={styles.assignLabel}>Type</span>
      <button
        type="button"
        className={`${styles.colPill} ${styles.typePill}`}
        data-ticket-type={current?.glyph ?? 'none'}
        aria-label={`Type: ${current?.name ?? 'No type'}. Change type`}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget.getBoundingClientRect())}
      >
        <span className={styles.colDot} />
        {current?.name ?? 'No type'}
      </button>
      <Popover open={anchor !== null} onClose={() => setAnchor(null)} anchor={anchor} placement="bottom-end">
        <Menu aria-label="Ticket type">
          {ticketTypeOptions(current, types).map((type) => (
            <MenuItem key={type.id} onClick={() => choose(type.id)} leftIcon={tick(type.id === current?.id)}>
              {type.name}
            </MenuItem>
          ))}
          <MenuItem onClick={() => choose(null)} leftIcon={tick(!current)}>
            No type
          </MenuItem>
        </Menu>
      </Popover>
    </div>
  );
}
